import type {Gateway} from '../../types';
import {
  toBindingSource,
  extendBindingSource,
  type BindingSource,
  type BindingContextTypeReference,
  type BindingSourceExtendableProps,
} from '../../primitives';

type Gateways = Gateway[];

export function getExpandedBindingSources(
  typeReference: BindingContextTypeReference,
  gateways: Gateways,
  includeDeprecated: boolean = false
): BindingSource[] {
  const bindingSources = getBindingSources(
    typeReference,
    gateways,
    includeDeprecated
  );
  return bindingSources.reduce<Array<BindingSource>>(
    (expanded, bindingSource) => {
      expanded.push(bindingSource);

      if (
        bindingSource.type === 'ItemRef' &&
        bindingSource.meta.preventExpansion !== true
      ) {
        const nestedBindingSources = getBindingSources(
          bindingSource.meta.type,
          gateways,
          includeDeprecated
        );
        expanded.push(
          ...nestedBindingSources.map(expandBindingSource(bindingSource))
        );
      }

      return expanded;
    },
    []
  );
}

export function getBindingSources(
  typeReference: BindingContextTypeReference,
  gateways: Gateways,
  includeDeprecated: boolean = false
): BindingSource[] {
  let activeGateway = null;

  for (let pluginIndex = 0; pluginIndex < gateways.length; pluginIndex++) {
    const gateway = gateways[pluginIndex];
    // @ts-expect-error - TS18048 - 'gateway' is possibly 'undefined'.
    if (gateway.getBindingContextType(typeReference)) {
      activeGateway = gateway;
      break;
    }
  }

  if (activeGateway === null) {
    return [];
  }

  // @ts-expect-error - TS18048 - 'activeGateway' is possibly 'undefined'.
  const bindableProperties = activeGateway.getBindableProperties(typeReference);
  const bindingSources: BindingSource[] = [];

  for (const bindableProperty of bindableProperties) {
    const bindingSource = toBindingSource(bindableProperty);
    if (!bindingSource.deprecated || includeDeprecated) {
      bindingSources.push(bindingSource);
    }
  }

  return bindingSources;
}

type ExpandBindingSource = (
  arg1: BindingSource
) => (arg1: BindingSource) => BindingSource;
const expandBindingSource: ExpandBindingSource = (parent) => {
  const {
    id: parentId,
    path: parentPath,
    queryPath: parentQueryPath,
    groupLabel,
    deprecated: parentDeprecated,
  } = parent;
  return (child: BindingSource) => {
    const id = expandIdentifier(parentId, child.id);
    const path = [...parentPath, ...child.path];
    const deprecated = parentDeprecated || child.deprecated;

    // if parent or child has no query path,
    // then the expanded binding source has no query path
    const queryPath =
      parentQueryPath.length > 0 && child.queryPath.length > 0
        ? [...parentQueryPath, ...child.queryPath]
        : [];

    const expandedProps: BindingSourceExtendableProps = {
      id,
      parentId,
      path,
      queryPath,
    };

    if (deprecated === true) {
      expandedProps.deprecated = deprecated;
    }

    if (groupLabel) {
      expandedProps.groupLabel = groupLabel;
    }

    return extendBindingSource(child, expandedProps);
  };
};

const ID_DELIMITER = ':::';
const expandIdentifier = (parentId: string, childId: string) =>
  `${parentId}${ID_DELIMITER}${childId}`;
